/**
 * <s-sticky-atc data-trigger="selector" data-hide-on="selector list">
 * Reveals its child bar once the trigger element has scrolled above the viewport,
 * and hides it again while any data-hide-on element (e.g. the main buy box or footer) is visible.
 * While hidden the bar is inert so it never traps focus or screen reader attention.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-sticky-atc-bar]');
    const trigger = document.querySelector(this.dataset.trigger || '');
    if (!this.bar || !trigger || !('IntersectionObserver' in window)) return;

    // On the product page, add whichever variant is selected in the main buy box.
    // Capture phase on this ancestor runs before s-product-form reads the form.
    if (this.dataset.syncVariant !== undefined) {
      this.addEventListener('submit', (event) => {
        const source = trigger.querySelector('input[name="id"]');
        const target = event.target.querySelector('input[name="id"]');
        if (source && target && source.value) target.value = source.value;
      }, true);
    }

    this.pastTrigger = false;
    this.blockers = new Set();

    this.triggerObserver = new IntersectionObserver(([entry]) => {
      this.pastTrigger = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.update();
    });
    this.triggerObserver.observe(trigger);

    const hideTargets = this.dataset.hideOn ? document.querySelectorAll(this.dataset.hideOn) : [];
    if (hideTargets.length) {
      this.hideObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.blockers.add(entry.target);
          else this.blockers.delete(entry.target);
        }
        this.update();
      }, { rootMargin: '0px 0px -15% 0px' });
      hideTargets.forEach((el) => this.hideObserver.observe(el));
    }

    this.update();
  }

  disconnectedCallback() {
    this.triggerObserver?.disconnect();
    this.hideObserver?.disconnect();
  }

  update() {
    const visible = this.pastTrigger && this.blockers.size === 0;
    if (visible) {
      this.bar.removeAttribute('inert');
      this.bar.removeAttribute('aria-hidden');
      this.bar.dataset.visible = 'true';
    } else {
      // Keep focus from being stranded inside a bar that is sliding away.
      if (this.bar.contains(document.activeElement)) document.activeElement.blur();
      this.bar.setAttribute('inert', '');
      this.bar.setAttribute('aria-hidden', 'true');
      this.bar.dataset.visible = 'false';
    }
  }
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', StickyAtc);
